import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { patientAPI, appointmentAPI, addressAPI, doctorNoteAPI, healthRecordAPI, healthJourneyAPI } from '../services/api';
import { doctorActionItemAPI } from '../services/doctorApi';
import PatientActivityTrail from '../components/PatientActivityTrail';
import HealthRecordsTab from '../components/healthrecords/HealthRecordsTab';
import EffectivePanelTab from '../components/healthrecords/EffectivePanelTab';
import PatientConfoundsTab from '../components/healthrecords/PatientConfoundsTab';
// LifestyleTab is the same Layer 3 AI lifestyle panel the triage report shows
// navigators (TriageReportModal) and doctors (DoctorTriageView reuses it the
// same way) — reused here rather than rebuilt so all three surfaces stay in
// sync with the one polling/rendering implementation.
import { LifestyleTab, injectCss as injectTriageReportCss } from '../components/healthrecords/TriageReportModal';
import { DoctorNotesTab } from '../components/doctor/DoctorTriageView';
import SubscriberLinkPanel from '../components/SubscriberLinkPanel';
import NavigatorReassignPanel from '../components/NavigatorReassignPanel';
import PlanBadge from '../components/PlanBadge';
import { HealthScoreHero } from './PatientTodayView';
import Icon from '../components/Icon';
import { useAuth } from '../context/AuthContext';
import { resolveNavigatorId } from '../utils/currentNavigator';
import { isTestItem } from '../utils/carePlanItems';

// "Lab Triage" tab hidden (Sep 2026) — LabTriageTab/LabTriageEngine never
// persist a TriageResult and can't run Model 3, so there's no honest way to
// offer a Claude/Deterministic/Both selector here. Code is untouched; only
// this entry point (and the standalone /lab-triage route in App.js) is
// removed. Re-add 'Lab Triage' to TABS + the LabTriageTab import + the
// panel render below to restore it.
const TABS = ['Overview', 'Appointments', 'Health Records', 'Current Markers', 'Lifestyle', 'Confounds', 'Addresses', 'Notes', 'Care Plan', 'Activity'];

// Matches PatientCarePlanPage.jsx's (patient-facing) PRIORITY_CONFIG so a
// navigator sees the same priority colours/labels the patient does.
const CARE_PLAN_PRIORITY = {
  URGENT:  { label: 'Urgent',  bg: 'var(--red-bg)',    color: 'var(--red)' },
  HIGH:    { label: 'High',    bg: 'var(--amber-bg)',  color: 'var(--amber)' },
  MEDIUM:  { label: 'Medium',  bg: 'var(--blue-bg)',   color: 'var(--blue)' },
  LOW:     { label: 'Low',     bg: 'var(--bg-subtle)', color: 'var(--text-muted)' },
  ROUTINE: { label: 'Routine', bg: 'var(--bg-subtle)', color: 'var(--text-muted)' },
};

const DOC_ICONS = {
  'Lab Results': '🧪',
  'Prescription': '💊',
  'Scan / Imaging': '🫀',
  'Discharge': '📋',
  'Insurance': '🛡️',
};

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [patient,      setPatient]      = useState(null);
  // The JWT's `sub` claim is the navigator's EMAIL, not a UUID (see
  // currentNavigator.js) — resolved once here rather than sending user.id
  // straight through as recordedByStaffId on the Confounds tab's log form.
  const [navigatorId,  setNavigatorId]  = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [addresses,    setAddresses]    = useState([]);
  const [records,      setRecords]      = useState([]);
  const [doctorNotes,  setDoctorNotes]  = useState([]);
  const [actionItems,  setActionItems]  = useState([]);
  const [completingId, setCompletingId] = useState(null);
  const [activeTab,    setActiveTab]    = useState(searchParams.get('tab') || 'Overview');
  const autoOpenRecordId = searchParams.get('recordId') || null;
  const [loading,      setLoading]      = useState(true);
  const [latestLifestyleTriage, setLatestLifestyleTriage] = useState(null);
  // Markers the treating doctor flagged as needing attention on the latest report.
  const [flaggedMarkers, setFlaggedMarkers] = useState([]);
  // The patient's health-score timeline (same graph as their Today screen).
  // null until loaded; stays null on failure, which just omits the graph.
  const [journey, setJourney] = useState(null);

  // Reschedule modal state — kept local to this component, not in URL/global state
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // appointment object or null
  const [rescheduleDate,   setRescheduleDate]   = useState('');
  const [rescheduleTime,   setRescheduleTime]   = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduling,     setRescheduling]     = useState(false);

  // Notifications card (ADMIN / HEALTH_NAVIGATOR only).
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError,  setNotifError]  = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [p, a, addr, notes, items] = await Promise.all([
          patientAPI.get(id),
          appointmentAPI.listByPatient(id).catch(() => ({ data: [] })),
          addressAPI.listByPatient(id).catch(() => ({ data: [] })),
          doctorNoteAPI.listByPatient(id).catch(() => ({ data: [] })),
          doctorActionItemAPI.listByPatient(id).catch(() => ({ data: [] })),
        ]);
        setPatient(p.data);
        setAppointments(Array.isArray(a.data) ? a.data : a.data?.content || []);
        setAddresses(Array.isArray(addr.data) ? addr.data : addr.data?.content || []);
        setDoctorNotes(Array.isArray(notes.data) ? notes.data : notes.data?.content || []);
        const rawActionItems = Array.isArray(items.data) ? items.data : items.data?.content || [];
        setActionItems(rawActionItems.filter(i => !isTestItem(i)));
        setRecords([]);
      } catch {
        navigate('/patients');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  // Health-score timeline for the Overview graph — same endpoint the
  // patient's own Today screen uses (staff may read it). Independent of
  // load() above: a failure here only means no graph, never a page error.
  useEffect(() => {
    if (!id) return;
    healthJourneyAPI.get(id)
      .then(res => setJourney(res.data || null))
      .catch(() => setJourney(null));
  }, [id]);

  // Lifestyle tab needs the patient's most recent BLOOD_TEST triage — the
  // only triage type LifestyleRecommendationService generates a lifestyle
  // plan for (see HealthRecordService.saveTriageResult). triage-history
  // already returns lifestyleRecommendation/lifestyleGeneratedAt/
  // healthRecordId per record, so no extra endpoint is needed — same
  // "failure here is just no content, never a page error" pattern as the
  // health-score timeline above.
  useEffect(() => {
    if (!id) return;
    healthRecordAPI.getTriageHistory(id)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
        const bloodTests = data.filter(t => t.triageType === 'BLOOD_TEST');
        const [latest] = bloodTests.sort((a, b) =>
          new Date(b.createdAt || b.drawDate) - new Date(a.createdAt || a.drawDate));
        setLatestLifestyleTriage(latest || null);
        setFlaggedMarkers(latest?.highlightedMarkers || []);
      })
      .catch(() => {});
  }, [id]);

  // LifestyleTab (reused from TriageReportModal) renders .rpt-* classes that
  // only exist once injected — TriageReportModal injects them itself when
  // it mounts, but here we're rendering the tab standalone, the same way
  // DoctorTriageView.jsx already has to.
  useEffect(() => { injectTriageReportCss(); }, []);

  // Resolved once on mount, same pattern ProfilePage.js uses — a failure here
  // just leaves the Confounds tab's log form without an author id (the
  // backend already treats recordedByStaffId as optional) rather than
  // blocking the page.
  useEffect(() => {
    resolveNavigatorId(user).then(setNavigatorId).catch(() => setNavigatorId(null));
  }, [user]);

  // Marks a care-plan action item done from the navigator portal. Backend
  // now attributes the audit row to whichever staff member actually clicked
  // this (via StaffActorResolver), not the item's original author, so this
  // shows up correctly as the navigator's own action on the Activity tab.
  // Un-checking a completed item asks for confirmation first, since it
  // reopens the task for the patient's care team.
  async function toggleActionItem(item) {
    const completed = !item.completed;
    if (!completed && !window.confirm('Mark this item as not done? It will be reopened.')) return;
    const itemId = item.id;
    setCompletingId(itemId);
    try {
      const { data } = await doctorActionItemAPI.update(itemId, { completed });
      setActionItems(prev => prev.map(i => (i.id === itemId ? data : i)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update item.');
    } finally {
      setCompletingId(null);
    }
  }

  // Posts into the same doctor_notes stream the doctor portal's Clinical
  // Notes tab already writes to (DoctorPortalService.addDoctorNote resolves
  // the caller's own role from doctorEmail now, so a navigator's post is
  // correctly tagged NAVIGATOR rather than mislabelled "Dr. …") — replaces
  // the old single-overwrite patients.notes textarea, which only the
  // navigator could ever see and which a doctor never wrote to.
  const handleAddNote = useCallback(async (content) => {
    // DoctorNotesTab's own save button stays disabled until onAdd resolves —
    // an uncaught rejection here would leave it stuck, so this must never throw.
    try {
      const { data } = await doctorNoteAPI.create({ patientId: id, doctorEmail: user?.email, content });
      setDoctorNotes(prev => [data, ...prev]);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to save note.');
    }
  }, [id, user?.email]);

  // Saves on every checkbox change. The endpoint requires both fields, so the
  // unchanged one is sent with its current value. Optimistic: the checkbox
  // flips immediately and is rolled back if the save fails. Goes through its
  // own endpoint, not patientAPI.update — that one writes no audit row.
  async function saveNotificationPreference(field, value) {
    const previous = {
      whatsappNotificationPreference: patient.whatsappNotificationPreference,
      appNotificationPreference:      patient.appNotificationPreference,
    };
    const body = {
      whatsappNotificationPreference: previous.whatsappNotificationPreference !== false,
      appNotificationPreference:      previous.appNotificationPreference !== false,
      [field]: value,
    };
    setNotifError('');
    setNotifSaving(true);
    setPatient(p => ({ ...p, ...body }));
    try {
      const { data } = await patientAPI.updateNotificationPreferences(id, body);
      setPatient(p => ({
        ...p,
        whatsappNotificationPreference: data?.whatsappNotificationPreference ?? body.whatsappNotificationPreference,
        appNotificationPreference:      data?.appNotificationPreference      ?? body.appNotificationPreference,
      }));
    } catch (err) {
      setPatient(p => ({ ...p, ...previous }));
      setNotifError(err?.response?.data?.message || 'Failed to update notification preferences.');
    } finally {
      setNotifSaving(false);
    }
  }

  if (loading) return (
    <AppShell breadcrumbs={[{ label: 'Patients', to: '/patients' }, { label: 'Loading…' }]}>
      <div className="loading-state"><div className="spinner" />Loading patient…</div>
    </AppShell>
  );

  if (!patient) return null;

  const initials = `${patient.firstName?.[0] || ''}${patient.lastName?.[0] || ''}`.toUpperCase();
  const fullName = `${patient.firstName} ${patient.lastName}`;
  // Health-score graph shows only when there is a scored report. With flagged
  // markers it takes two of the three columns and the Flagged card sits beside it.
  const showScoreGraph = !!journey && (journey.milestones || []).some(m => m.score != null);
  const upcoming = appointments.filter(a => ['UPCOMING', 'CONFIRMED', 'PENDING', 'SCHEDULED', 'ARRIVED', 'IN_CONSULTATION'].includes(a.status?.toUpperCase()));
  const past     = appointments.filter(a => ['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(a.status?.toUpperCase()));

  // Undefined = ON: the backend defaults both to true, and a backend that
  // predates these fields omits them entirely.
  const canManageNotifications = user?.role === 'ADMIN' || user?.role === 'HEALTH_NAVIGATOR';
  const whatsappOn = patient.whatsappNotificationPreference !== false;
  const appOn      = patient.appNotificationPreference !== false;
  const pausedChannels = [!whatsappOn && 'WhatsApp messages', !appOn && 'In-app (bell) notifications'].filter(Boolean);

  return (
    <AppShell breadcrumbs={[
      { label: 'Patients', to: '/patients' },
      { label: fullName },
    ]}>
      <span className="back-link fade-in-up" onClick={() => navigate('/patients')}>
        ← Back to Patients
      </span>

      {/* Hero */}
      <div className="patient-hero fade-in-up delay-1">
        <div className="patient-avatar-lg">{initials}</div>
        <div style={{ flex: 1 }}>
          <div className="patient-name-lg">{fullName}</div>
          {/* Short display code (PRV-7K3M9Q) — what a navigator reads out. The raw
              UUID is an internal key and stays in the URL only. */}
          {patient.patientCode && (
            <div className="patient-meta-lg">
              <span className="mono">{patient.patientCode}</span>
            </div>
          )}
          <div className="patient-meta-lg">
            {patient.gender || 'Gender not on file'}
            &nbsp;·&nbsp;Born {patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            {patient.bloodType && <>&nbsp;·&nbsp;Blood: {patient.bloodType}</>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className={`badge ${patient.status === 'ACTIVE' ? 'badge-active' : patient.status === 'INACTIVE' ? 'badge-inactive' : 'badge-pending'}`}>
              {patient.status || 'Pending'}
            </span>
            {/* Sits in the hero rather than on one tab, so a navigator sees
                whether this is a Guardian Dubai customer — and which plan they
                are on — from every tab of the patient record. */}
            <PlanBadge patient={patient} />
            {patient.riskLevel && (
              <span className="badge badge-purple">⚠ {patient.riskLevel} Risk</span>
            )}
            {/* DEF-023: legacy/incomplete record — nudge staff to fix it via
                the existing Edit action rather than the app assuming a gender. */}
            {!patient.gender && (
              <span className="badge badge-pending" title="Click Edit to set this patient's gender">
                ⚠️ Gender missing — please update
              </span>
            )}
            {pausedChannels.length > 0 && (
              <span className="badge badge-pending" title={`Off: ${pausedChannels.join(', ')}`}>
                🔕 Notifications paused
              </span>
            )}
          </div>
        </div>
        <div className="patient-hero-actions">
          <button className="btn btn-ghost" style={{ color: 'var(--text-on-brand-dark)', borderColor: 'rgba(255,255,255,0.3)' }}
            onClick={() => navigate(`/patients/${id}/edit`)}>
            ✏ Edit
          </button>
          <button className="btn btn-ghost" style={{ color: 'var(--text-on-brand-dark)', borderColor: 'rgba(255,255,255,0.3)' }}
            onClick={() => navigate(`/patients/${id}/pre-call-briefing`)}>
            📋 View Call Brief
          </button>
          <button className="btn btn-ghost" style={{ color: 'var(--text-on-brand-dark)', borderColor: 'rgba(255,255,255,0.3)' }}
            onClick={() => navigate(`/patients/${id}/chat`)}>
            💬 Chat
          </button>
          <button className="btn btn-ghost" style={{ color: 'var(--text-on-brand-dark)', borderColor: 'rgba(255,255,255,0.3)' }}
            onClick={() => navigate(`/patients/${id}/wallet`)}>
            👛 Wallet Ledger
          </button>
          <button className="btn btn-accent"
            onClick={() => navigate(`/appointments/new?patientId=${id}`)}>
            + Book Appointment
          </button>
          <button className="btn btn-accent"
            onClick={() => navigate(`/lab-tests/new?patientId=${id}`)}>
            + Book Lab Test
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-bar fade-in-up delay-2">
        {TABS.map(t => (
          <button key={t} className={`tab-btn ${activeTab === t ? 'active' : ''}`}
            onClick={() => setActiveTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ──
          Three equal-height columns. Order fills every row: Flagged (when
          present) beside the health-score graph on top (graph alone is full
          width); Contact / Medical / Navigator; Guardian /
          Notifications / Quick Stats; Appointments across the bottom. */}
      {activeTab === 'Overview' && (
        <div className="detail-grid detail-grid--aligned">
          {/* The same health-score timeline graph the patient sees on their
              Today screen. Only when at least one scored report exists. */}
          {showScoreGraph && (
            <div className={flaggedMarkers.length > 0 ? 'detail-grid-span' : 'detail-grid-full'}>
              <HealthScoreHero patient={patient} journey={journey} showGreeting={false} allowMetricToggle />
            </div>
          )}
          {flaggedMarkers.length > 0 && (
            <div className={`card fade-in-up ${showScoreGraph ? 'detail-grid-beside' : 'detail-grid-full'}`}>
              <div className="card-title"><span className="card-title-icon"><Icon name="alert" size={15} /></span>Flagged by the doctor</div>
              <div className="field-text">
                Markers the doctor picked as needing attention on the latest report.
                A retest has been suggested for each, in the Care Plan.
              </div>
              {/* Wraps whole chips onto new lines: this card can be one narrow
                  column beside the graph, where squeezed chips broke mid-word. */}
              <div className="toolbar" style={{ marginTop: 10, flexWrap: 'wrap' }}>
                {flaggedMarkers.map(m => (
                  <span key={m.markerName} className="badge badge-moderate" style={{ whiteSpace: 'nowrap' }}>{m.markerName}</span>
                ))}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setActiveTab('Care Plan')}>
                  View care plan →
                </button>
              </div>
            </div>
          )}
          <div className="card fade-in-up">
            <div className="card-title"><span className="card-title-icon"><Icon name="phone" size={15} /></span>Contact Details</div>
            <InfoRow label="Mobile"    value={patient.phone} />
            <InfoRow label="Email"     value={patient.email} />
            <InfoRow label="Emergency" value={patient.emergencyContactName} />
            <InfoRow label="Emerg. Ph" value={patient.emergencyContactPhone} />
          </div>
          <div className="card fade-in-up delay-1">
            <div className="card-title"><span className="card-title-icon"><Icon name="pill" size={15} /></span>Medical Summary</div>
            <InfoRow label="Diagnosis"    value={patient.medicalHistory} />
            <InfoRow label="Allergies"    value={patient.allergies} danger />
            <InfoRow label="Medications"  value={patient.currentMedications} />
            <InfoRow label="Blood Group"  value={patient.bloodType} />
          </div>
          <div className="card fade-in-up delay-2">
            <div className="card-title"><span className="card-title-icon"><Icon name="compass" size={15} /></span>Navigator Info</div>
            <InfoRow label="Navigator"    value={patient.navigatorName || patient.navigator?.name} />
            <InfoRow label="Enrolled"     value={patient.enrolledSince ? new Date(patient.enrolledSince).toLocaleDateString('en-IN') : null} />
            <InfoRow label="Program"      value={patient.program} />
            <InfoRow label="Risk Level"   value={patient.riskLevel} />
            {/* Any navigator can move this patient to any other navigator —
                not just their own patients, not admin-only. See
                NavigatorReassignPanel.jsx / the backend's
                NavigatorAssignmentService javadoc for why. */}
            <NavigatorReassignPanel
              patientId={patient.id}
              currentNavigatorName={patient.navigatorName || patient.navigator?.name}
              onReassigned={(name) => setPatient(p => ({ ...p, navigatorName: name }))}
            />
          </div>

          {/* 30 Jul 2026 — new. Feature #1: Navigator can link/detach a
              patient's Guardian Dubai subscriber. Placed alongside Navigator
              Info since it's conceptually the same kind of "who manages this
              patient" assignment. patient.subscriberId comes from
              PatientDTO.Response (added 30 Jul 2026 specifically for this). */}
          <div className="card fade-in-up delay-2">
            <div className="card-title"><span className="card-title-icon">🇦🇪</span>Guardian Dubai</div>
            <SubscriberLinkPanel patientId={patient.id} currentSubscriberId={patient.subscriberId} />
          </div>

          {canManageNotifications && (
            <div className="card fade-in-up delay-2">
              <div className="card-title"><span className="card-title-icon"><Icon name="bell" size={15} /></span>Notifications</div>
              {notifError && <div className="alert alert-error">{notifError}</div>}
              <label className="form-check">
                <input type="checkbox" checked={whatsappOn} disabled={notifSaving}
                  onChange={e => saveNotificationPreference('whatsappNotificationPreference', e.target.checked)} />
                WhatsApp messages
              </label>
              <label className="form-check">
                <input type="checkbox" checked={appOn} disabled={notifSaving}
                  onChange={e => saveNotificationPreference('appNotificationPreference', e.target.checked)} />
                In-app (bell) notifications
              </label>
            </div>
          )}

          <div className="card fade-in-up delay-2">
            <div className="card-title"><span className="card-title-icon"><Icon name="trend" size={15} /></span>Quick Stats</div>
            <InfoRow label="Total Appts"    value={appointments.length || '0'} />
            <InfoRow label="Upcoming"       value={upcoming.length || '0'} />
            <InfoRow label="Completed"      value={past.filter(a => a.status?.toUpperCase() === 'COMPLETED').length || '0'} />
            <InfoRow label="Addresses"      value={addresses.length || '0'} />
          </div>
          <div className="card fade-in-up delay-1 detail-grid-full">
            <div className="card-title"><span className="card-title-icon"><Icon name="calendar" size={15} /></span>Upcoming / Recent Appointments</div>
            {upcoming.length === 0
              ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No upcoming or recent appointments</div>
              /* .stack-table, not a bare table: four columns cannot fit a phone,
                 and .main-content clips horizontal overflow, so the Status cell
                 was cut to "SCH…" with no way to scroll to it. */
              : <table className="stack-table">
                  <thead><tr><th>Date</th><th>Doctor / Navigator</th><th>Type</th><th>Status</th></tr></thead>
                  <tbody>
                    {upcoming.slice(0, 3).map(a => (
                      <tr key={a.id}>
                        <td data-label="Date">{new Date(a.appointmentDate || a.date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</td>
                        <td data-label="Doctor / Navigator">{a.doctorName || a.doctor?.name || (a.navigatorName ? `${a.navigatorName} (Navigator)` : '—')}</td>
                        <td data-label="Type">{a.appointmentType || a.type || '—'}</td>
                        <td data-label="Status"><span className={`badge badge-${a.status?.toLowerCase() === 'confirmed' ? 'confirmed' : a.status?.toLowerCase() === 'scheduled' ? 'confirmed' : 'pending'}`}>{a.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            }
          </div>

        </div>
      )}

      {/* ── APPOINTMENTS ── */}
      {activeTab === 'Appointments' && (
        <div className="fade-in-up">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
            <div className="stat-card"><div className="stat-label">Total</div><div className="stat-value" style={{ fontSize: 24 }}>{appointments.length}</div></div>
            <div className="stat-card"><div className="stat-label">Upcoming</div><div className="stat-value" style={{ fontSize: 24 }}>{upcoming.length}</div></div>
            <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value" style={{ fontSize: 24 }}>{past.filter(a => a.status?.toUpperCase() === 'COMPLETED').length}</div></div>
            <div className="stat-card"><div className="stat-label">Cancelled</div><div className="stat-value" style={{ fontSize: 24, color: appointments.some(a => ['CANCELLED','NO_SHOW'].includes(a.status?.toUpperCase())) ? 'var(--red)' : undefined }}>{appointments.filter(a => ['CANCELLED','NO_SHOW'].includes(a.status?.toUpperCase())).length}</div></div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date & Time</th><th>Doctor / Navigator</th><th>Type</th><th>Mode</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {appointments.length === 0
                  ? <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="calendar" size={20} /></div><div className="empty-title">No appointments yet</div></div></td></tr>
                  : appointments.map(a => (
                    <tr key={a.id}>
                      <td>
                        <strong>{new Date(a.appointmentDate || a.date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</strong>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.appointmentTime || a.time || ''}</div>
                      </td>
                      <td>{a.doctorName || a.doctor?.name || (a.navigatorName ? `${a.navigatorName} (Navigator)` : '—')}</td>
                      <td>{a.appointmentType || a.type || '—'}</td>
                      <td>{a.consultationMode || a.mode || '—'}</td>
                      <td>
                        <span className={`badge badge-${
                          a.status?.toUpperCase() === 'COMPLETED' ? 'active' :
                          a.status?.toUpperCase() === 'CANCELLED' || a.status?.toUpperCase() === 'NO_SHOW' ? 'inactive' :
                          a.status?.toUpperCase() === 'CONFIRMED' ? 'confirmed' :
                          a.status?.toUpperCase() === 'IN_CONSULTATION' ? 'purple' : 'pending'
                        }`}>{a.status}</span>
                      </td>
                      <td>
                        {['UPCOMING','CONFIRMED','PENDING','SCHEDULED'].includes(a.status?.toUpperCase())
                          ? <div style={{ display: 'flex', gap: 6 }}>
                              <button className="btn btn-secondary btn-sm" onClick={() => {
                                setRescheduleTarget(a);
                                setRescheduleDate(a.appointmentDate || a.date || '');
                                setRescheduleTime(a.appointmentTime || a.time || '');
                                setRescheduleReason('');
                              }}>
                                📅 Reschedule
                              </button>
                              <button className="btn btn-danger btn-sm" onClick={async () => {
                                if (!window.confirm('Cancel this appointment?')) return;
                                try {
                                  const { appointmentAPI: aAPI } = await import('../services/api');
                                  await aAPI.cancel(a.id);
                                  setAppointments(prev => prev.map(x => x.id === a.id ? {...x, status: 'CANCELLED'} : x));
                                } catch { alert('Failed to cancel.'); }
                              }}>Cancel</button>
                            </div>
                          : <button className="btn btn-ghost btn-sm">Notes</button>
                        }
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── HEALTH RECORDS ── */}
      {activeTab === 'Health Records' && (
        <div className="fade-in-up">
          <HealthRecordsTab patient={patient} autoOpenRecordId={autoOpenRecordId} />
        </div>
      )}

      {/* ── CURRENT MARKERS ── */}
      {activeTab === 'Current Markers' && (
        <div className="fade-in-up">
          <EffectivePanelTab patient={patient} />
        </div>
      )}

      {/* ── LIFESTYLE ── */}
      {activeTab === 'Lifestyle' && (
        <div className="fade-in-up">
          {latestLifestyleTriage
            ? (
              <LifestyleTab
                triage={latestLifestyleTriage}
                patientId={id}
                recordId={latestLifestyleTriage.healthRecordId}
              />
            )
            : (
              <div className="card">
                <div className="empty-state">
                  <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="heart" size={20} /></div>
                  <div className="empty-title">No lifestyle recommendation yet</div>
                  <div className="empty-desc">
                    A personalised lifestyle plan generates automatically once a blood test triage is completed.
                  </div>
                </div>
              </div>
            )}
        </div>
      )}

      {/* ── CONFOUNDS ── */}
      {activeTab === 'Confounds' && (
        <div className="fade-in-up">
          <PatientConfoundsTab patient={patient} currentUserId={navigatorId} source="NAVIGATOR" />
        </div>
      )}

      {/* ── ADDRESSES ── */}
      {activeTab === 'Addresses' && (
        <div className="fade-in-up">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 18 }}>
            <button className="btn btn-primary" onClick={() => navigate(`/patients/${id}/addresses`)}>
              + Add Address
            </button>
          </div>
          {addresses.length === 0
            ? <div className="card"><div className="empty-state"><div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="pin" size={20} /></div><div className="empty-title">No addresses</div><div className="empty-desc">Add an address for this patient</div></div></div>
            : <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                {addresses.map(addr => (
                  <div className={`address-card ${addr.isDefault ? 'primary' : ''}`} key={addr.id}>
                    {addr.isDefault && (
                      <div style={{ position: 'absolute', top: 14, right: 14 }}>
                        <span className="badge badge-confirmed">Primary</span>
                      </div>
                    )}
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 10 }}>
                      🏠 {addr.addressType || 'Address'}
                    </div>
                    <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text-primary)' }}>
                      <strong>{addr.street || addr.addressLine1}</strong><br />
                      {addr.area || addr.addressLine2}<br />
                      {addr.city}, {addr.state} – {addr.pinCode || addr.zip}
                    </div>
                    <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
                      <button className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/patients/${id}/addresses?edit=${addr.id}`)}>
                        ✏ Edit
                      </button>
                      <button className="btn btn-danger btn-sm"
                        onClick={async () => {
                          if (!window.confirm('Remove this address?')) return;
                          try {
                            const { addressAPI: aAPI } = await import('../services/api');
                            await aAPI.delete(id, addr.id);
                            setAddresses(prev => prev.filter(a => a.id !== addr.id));
                          } catch { alert('Failed to delete.'); }
                        }}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      )}

      {/* ── NOTES ── */}
      {/* Same doctor_notes stream the doctor portal's Clinical Notes tab
          writes to (DoctorNotesTab, reused as-is) — a navigator can now post
          into it too, correctly tagged NAVIGATOR (see handleAddNote), instead
          of the old read-only doctor feed plus a separate single-overwrite
          patients.notes textarea only navigators could ever see. */}
      {activeTab === 'Notes' && (
        <div className="fade-in-up card">
          <DoctorNotesTab notes={doctorNotes} onAdd={handleAddNote} />
        </div>
      )}

      {/* Same doctor_action_items rows CarePlanAssembler shows the patient on
          /patient/care-plan, but the raw list (no dedup/suppression cap) and,
          unlike that read-only patient view, a navigator can mark one done
          here — PUT /api/doctor/action-items/{id} is authorized for
          HEALTH_NAVIGATOR too, not just DOCTOR. [TEST]-tagged items (see
          utils/carePlanItems.js) are still filtered out — those are test-run
          artifacts written against real patient records, not care plan
          content a navigator should see or act on. */}
      {activeTab === 'Care Plan' && (
        <div className="fade-in-up">
          <div className="card">
            <div className="card-title">🗓️ Care Plan Action Items</div>
            {actionItems.length === 0
              ? <div className="empty-state" style={{ padding: '24px 0' }}>
                  <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="clipboard" size={20} /></div>
                  <div className="empty-title">No action items yet</div>
                  <div className="empty-desc">Items added by a doctor, navigator, or triage will appear here.</div>
                </div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {actionItems.map(item => {
                    const cfg = CARE_PLAN_PRIORITY[item.priority] || CARE_PLAN_PRIORITY.ROUTINE;
                    return (
                      <div key={item.id} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '12px 14px', borderRadius: 8,
                        background: 'var(--bg-subtle)',
                        border: `1px solid ${item.completed ? 'var(--bg-hover)' : cfg.bg}`,
                        borderLeft: `4px solid ${item.completed ? 'var(--bg-hover)' : cfg.color}`,
                        opacity: item.completed ? 0.7 : 1,
                      }}>
                        <button
                          type="button"
                          aria-label={item.completed ? 'Completed — click to mark as not done' : 'Mark as done'}
                          disabled={completingId === item.id}
                          onClick={() => toggleActionItem(item)}
                          style={{
                            width: 22, height: 22, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                            border: `2px solid ${item.completed ? 'var(--green)' : cfg.color}`,
                            background: item.completed ? 'var(--green-bg)' : 'var(--bg-surface)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', padding: 0,
                          }}
                        >
                          {item.completed && (
                            <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                              <path d="M1 4L3.5 6.5L9 1" stroke="var(--green)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 13.5, lineHeight: 1.6,
                            color: item.completed ? 'var(--text-muted)' : 'var(--text-primary)',
                            textDecoration: item.completed ? 'line-through' : 'none',
                            marginBottom: 6,
                          }}>
                            {item.description}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '3px 9px',
                              borderRadius: 20, letterSpacing: '0.05em', textTransform: 'uppercase',
                              background: cfg.bg, color: cfg.color,
                            }}>
                              {cfg.label}
                            </span>
                            {item.createdAt && (
                              <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                                Added {new Date(item.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                            {item.dueDate && (
                              <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                                Due {new Date(item.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                            {item.completed
                              ? <span style={{ fontSize: 11.5, color: 'var(--green)' }}>✓ Completed</span>
                              : completingId === item.id
                                ? <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Marking done…</span>
                                : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        </div>
      )}

      {/* Lab Triage tab removed (Sep 2026) — see the TABS comment above. */}

      {/* The audit trail had thirteen writers and no reader: everything
          recorded about a patient was reachable only from a SQL client.
          Read-only — the backend treats the table as append-only. */}
      {activeTab === 'Activity' && (
        <div className="fade-in-up">
          <PatientActivityTrail patientId={id} />
        </div>
      )}

      {/* ── RESCHEDULE MODAL ── */}
      {rescheduleTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Reschedule appointment"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(26,40,65,.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !rescheduling && setRescheduleTarget(null)}
        >
          <div
            style={{
              background: 'var(--bg-surface)', borderRadius: 12, padding: 24,
              width: 420, maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,.2)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: 'var(--text-primary)' }}>
              Reschedule Appointment
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18 }}>
              Currently: {new Date(rescheduleTarget.appointmentDate || rescheduleTarget.date)
                .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              {' · '}{rescheduleTarget.appointmentTime || rescheduleTarget.time}
            </div>

            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 4 }}>
              New date
            </label>
            <input
              type="date"
              className="form-input"
              style={{ width: '100%', marginBottom: 14 }}
              value={rescheduleDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={e => setRescheduleDate(e.target.value)}
            />

            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 4 }}>
              New time
            </label>
            <input
              type="time"
              className="form-input"
              style={{ width: '100%', marginBottom: 14 }}
              value={rescheduleTime}
              onChange={e => setRescheduleTime(e.target.value)}
            />

            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: 4 }}>
              Reason (optional)
            </label>
            <textarea
              className="form-textarea"
              style={{ width: '100%', minHeight: 70, marginBottom: 18 }}
              placeholder="e.g. Patient requested a later slot"
              value={rescheduleReason}
              onChange={e => setRescheduleReason(e.target.value)}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="btn btn-ghost"
                disabled={rescheduling}
                onClick={() => setRescheduleTarget(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={rescheduling || !rescheduleDate || !rescheduleTime}
                onClick={async () => {
                  setRescheduling(true);
                  try {
                    const { data: newAppt } = await appointmentAPI.reschedule(rescheduleTarget.id, {
                      newDate: rescheduleDate,
                      newTime: rescheduleTime,
                      reason:  rescheduleReason || null,
                    });
                    // Mark the old row CANCELLED in local state, append the new row —
                    // mirrors exactly what the backend did (cancel + create new).
                    setAppointments(prev => [
                      ...prev.map(x => x.id === rescheduleTarget.id ? { ...x, status: 'CANCELLED' } : x),
                      newAppt,
                    ]);
                    setRescheduleTarget(null);
                  } catch (err) {
                    alert(err?.response?.data?.message || 'Failed to reschedule appointment.');
                  } finally {
                    setRescheduling(false);
                  }
                }}
              >
                {rescheduling ? 'Rescheduling…' : 'Confirm Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

    </AppShell>
  );
}

function InfoRow({ label, value, danger }) {
  return (
    <div className="info-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--bg-subtle)', fontSize: 13 }}>
      <span style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0 }}>{label}</span>
      {/* minWidth: 0 is load-bearing: a flex item's default min-width is `auto`,
          which means it refuses to shrink below its own content's width no
          matter what maxWidth says. An unbroken token like an email address
          has nowhere to wrap, so without this it pushed straight past the
          card's edge instead of respecting the 60% cap. overflowWrap covers
          the same case for the rare value even a full row can't fit. */}
      <span style={{ color: danger && value ? 'var(--red)' : 'var(--text-primary)', fontWeight: 600, textAlign: 'right', maxWidth: '60%', minWidth: 0, overflowWrap: 'anywhere' }}>
        {value || <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>—</span>}
      </span>
    </div>
  );
}
